import { joinURL, parseURL } from 'ufo'
import type { MediaItem } from '~/composables/types/event'

/** media.gokonfetti.com/{id}/{segment}/{filename} — segment may be -, Nx0, NxM */
const KONFETTI_MEDIA_PATH = /^\/(\d+)\/([^/]+)\/(.+)$/i

export interface KonfettiGalleryImageSource {
  id?: number
  filename?: string
  cropped?: string
  original?: string
  thumb?: string
}

export const replaceSubdomainToProd = (url: string) =>
  url
    ? url.replace(
        'https://media.hml.gokonfetti.com/',
        'https://media.gokonfetti.com/'
      )
    : url

export const buildKonfettiMediaUrl = (
  mediaId: number | string,
  filename: string,
  width: number,
  host = 'https://media.gokonfetti.com'
): string => {
  const base = host.replace(/\/$/, '')
  return `${base}/${mediaId}/${width}x0/${filename}`
}

/**
 * Rewrites the size segment of a media URL to `{width}x0` — scale to width,
 * aspect ratio preserved.
 *
 * Do NOT reach for an `NxM` segment to fill a square box: the media service
 * *distorts* to the exact dimensions rather than cropping, so `120x120` from a
 * landscape source is the whole image squashed, not a centre crop. Square boxes
 * size by width and crop in CSS with `object-cover`, which means the file width
 * has to cover `box x DPR x sourceAspectRatio` for the *height* to fill.
 */
export const toKonfettiWidth = (
  src: string,
  width: number,
  baseURL = 'https://media.gokonfetti.com'
): string => {
  if (!src) {
    return src
  }

  const segment = `${width}x0`
  const base = baseURL.replace(/\/$/, '')

  if (src.startsWith('http://') || src.startsWith('https://')) {
    const u = parseURL(src)
    const pathname = u.pathname || ''
    const m = pathname.match(KONFETTI_MEDIA_PATH)
    if (!m) {
      return src
    }
    const [, id, , filename] = m
    const host = u.host ? `${u.protocol}//${u.host}` : base
    return joinURL(host, `/${id}/${segment}/${filename}`)
  }

  const pathname = src.startsWith('/') ? src : `/${src}`
  const m = pathname.match(KONFETTI_MEDIA_PATH)
  if (!m) {
    return joinURL(base, pathname)
  }
  const [, id, , filename] = m
  return joinURL(base, `/${id}/${segment}/${filename}`)
}

export interface KonfettiWidthOptions {
  /**
   * Keep the host the API returned instead of forcing production. Required for
   * review media, which only exists on the host of the environment that served
   * it — homolog review assets 404 on media.gokonfetti.com.
   */
  preserveHost?: boolean
}

export const toKonfettiWidthForGalleryItem = (
  item: KonfettiGalleryImageSource,
  width: number,
  { preserveHost = false }: KonfettiWidthOptions = {}
): string => {
  const source = item.cropped || item.thumb || item.original || ''
  const baseUrl = preserveHost ? source : replaceSubdomainToProd(source)

  if (baseUrl) {
    const pathname = baseUrl.startsWith('http')
      ? parseURL(baseUrl).pathname || ''
      : baseUrl.startsWith('/')
        ? baseUrl
        : `/${baseUrl}`

    if (KONFETTI_MEDIA_PATH.test(pathname)) {
      return toKonfettiWidth(baseUrl, width)
    }

    if (!item.id || !item.filename) {
      return baseUrl
    }
  }

  if (item.id && item.filename) {
    return buildKonfettiMediaUrl(item.id, item.filename, width)
  }

  return baseUrl
}

/**
 * Same as `toKonfettiWidthForGalleryItem`, for the `MediaItem` shape the API
 * returns for review media. `crop.cropImg` is the only media-service URL on
 * those items — `thumb`/`cdn_url`/`url` point at CloudFront or S3 originals,
 * which carry no resize segment and so cannot be rewritten.
 */
export const toKonfettiWidthForMediaItem = (
  item: MediaItem,
  width: number
): string =>
  toKonfettiWidthForGalleryItem(
    {
      id: item.id,
      filename: item.file_name || '',
      cropped: item.crop?.cropImg || '',
      original: item.cdn_url || item.url || '',
      thumb: item.thumb || item.cdn_url || item.url || '',
    },
    width,
    { preserveHost: true }
  )
